import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn, Button, ScrollArea } from '@scio3/ui';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth-store';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';
import { SCIO1_STATE_MAP } from '@/components/iframe/scio1-route-map';
import type { LucideIcon } from 'lucide-react';

import { PlanGate } from '@/components/shared';
import type { PlanGateProps } from '@/components/shared';
import type { getCompanyFeatures } from '@/hooks/useCompanyFeatures';
import {
  UserCircle,
  Building2,
  Users,
  CreditCard,
  Wallet,
  Receipt,
  Building,
  Ticket,
  ShoppingCart,
  ClipboardList,
  KeyRound,
  SlidersHorizontal,
  Calendar,
  CheckSquare,
  Database,
  Rss,
  Link,
  BarChart3,
  Bell,
  Cpu,
  Radio,
  MapPin,
  Key,
  Plug,
  Shield,
  Fingerprint,
  ScrollText,
} from 'lucide-react';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  /** Controls sidebar visibility. Undefined = always visible. */
  show?: boolean;
  /**
   * Direct-URL access gate — checked when the page is accessed via URL.
   * - undefined: falls back to `show` (hidden from sidebar = blocked via URL)
   * - boolean: overrides `show` for access control (e.g. hidden from sidebar but accessible if permission passes)
   */
  accessGate?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Routes that render native React components (not iframe).
 *  Also used by AppShell to decide when to mount the scio1 modal adapter iframe. */
export const NATIVE_SETTINGS_SLUGS = new Set<string>();
if (import.meta.env.VITE_USE_NATIVE_PROFILE === 'true')
  NATIVE_SETTINGS_SLUGS.add('personal-profile');
if (import.meta.env.VITE_USE_NATIVE_COMPANY_PROFILE === 'true')
  NATIVE_SETTINGS_SLUGS.add('company-profile');
if (import.meta.env.VITE_USE_NATIVE_EXTERNAL_COMS === 'true')
  NATIVE_SETTINGS_SLUGS.add('external-coms');
if (import.meta.env.VITE_USE_NATIVE_PREFERENCES === 'true')
  NATIVE_SETTINGS_SLUGS.add('preference-settings');
if (import.meta.env.VITE_USE_NATIVE_APIKEYS === 'true') NATIVE_SETTINGS_SLUGS.add('apikeys');
if (import.meta.env.VITE_USE_NATIVE_FEEDS_ADMIN === 'true')
  NATIVE_SETTINGS_SLUGS.add('feeds-admin');
if (import.meta.env.VITE_USE_NATIVE_DATASOURCES === 'true')
  NATIVE_SETTINGS_SLUGS.add('datasources-admin');
if (import.meta.env.VITE_USE_NATIVE_POP_SETTINGS === 'true')
  NATIVE_SETTINGS_SLUGS.add('proof-of-play-settings');
if (import.meta.env.VITE_USE_NATIVE_INTEGRATIONS === 'true')
  NATIVE_SETTINGS_SLUGS.add('integrations');
if (import.meta.env.VITE_USE_NATIVE_MONITORING_ALERT_RULES === 'true')
  NATIVE_SETTINGS_SLUGS.add('monitoring-alert-rules');

/** Company feature flags type — derived from getCompanyFeatures return. */
type CompanyFeatures = ReturnType<typeof getCompanyFeatures>;

/** PlanGateProps with planFeature narrowed to actual feature flag keys. */
type SettingsPlanGateConfig = Omit<PlanGateProps, 'planFeature'> & {
  planFeature: keyof CompanyFeatures;
};

/**
 * Settings slugs that show a native plan gate when their feature flag is disabled.
 *
 * Only includes iframe-only pages that lack a scio1 plan gate.
 * - API Keys & Data Sources: handled by their own native page components
 * - SAML & Monitoring Alerts: scio1 handles plan gating inside the iframe
 */
export const SETTINGS_PLAN_GATES: Record<string, SettingsPlanGateConfig> = {
  'approval-list': {
    planFeature: 'approvalWorkflow',
    minPlanRequired: 'proPlus',
    featureLabel: 'Approval Requests',
    planLabel: 'Pro Plus Plan',
  },
};

/** Settings slugs that render without the sidebar (full-width iframe pages). */
const FULL_WIDTH_SETTINGS_SLUGS = new Set<string>([
  'order-device',
  'payment-intent',
  'payment-thankyou',
]);

/** scio1-only slugs that redirect to a scio3 equivalent (relative to /app/s/). */
const SETTINGS_REDIRECTS: Record<string, string> = {
  'android-devices': '/app/s/order-device',
  'free-devices': '/app/s/order-device',
  'subscription-thankyou': '/app/s/personal-profile',
  'subscription-init-thankyou': '/app/s/personal-profile',
  'proof-of-play-redirect': '/app/s/proof-of-play-settings',
  'request-apis': '/app/s/personal-profile',
};

function useSettingsNav(): NavGroup[] {
  const features = useCompanyFeatures();
  const { isRootAdmin, isRootSuperAdmin, isTeamAdmin, currentTeam } = usePermissions();
  const me = useAuthStore((s) => s.me);
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  const canApprove = isTeamAdmin || !!currentTeam?.permissions?.playlistCanApprove;

  return [
    {
      label: 'Account',
      items: [
        { icon: UserCircle, label: 'Personal Profile', path: '/app/s/personal-profile' },
        {
          icon: Building2,
          label: 'Company Settings',
          path: '/app/s/company-profile',
          show: isRootAdmin,
        },
        { icon: Users, label: 'Account Members', path: '/app/s/account-members' },
      ],
    },
    {
      label: 'Billing & Orders',
      items: [
        {
          icon: CreditCard,
          label: 'Subscription Plan',
          path: '/app/s/subscription-plan',
          show: isRootAdmin,
        },
        { icon: Wallet, label: 'Payment Settings', path: '/app/s/payment', show: isRootAdmin },
        {
          icon: Receipt,
          label: 'Billing History',
          path: '/app/s/payment-history',
          show: isRootAdmin,
        },
        {
          icon: Building,
          label: 'Sub-Account Billing',
          path: '/app/s/sub-account-billing',
          show: isRootAdmin,
        },
        {
          icon: Ticket,
          label: 'Coupons',
          path: '/app/s/coupons',
          show: false,
          accessGate: isRootAdmin,
        },
        {
          icon: ShoppingCart,
          label: 'Order Devices',
          path: '/app/s/order-device',
          show: isRootAdmin,
        },
        {
          icon: ClipboardList,
          label: 'Order History',
          path: '/app/s/ordered-device',
          show: isRootAdmin,
        },
      ],
    },
    {
      label: 'Settings',
      items: [
        {
          icon: KeyRound,
          label: 'Credentials',
          path: '/app/s/credentials',
          // Hidden from sidebar — but accessible via direct URL if user has permission
          show: false,
          accessGate: isRootAdmin && (features.apiKey || features.apiAccessToken),
        },
        {
          icon: SlidersHorizontal,
          label: 'Preferences',
          path: '/app/s/preference-settings',
          show: isRootSuperAdmin && !isWhiteLabel,
        },
        {
          icon: Calendar,
          label: 'Operational Schedule',
          path: '/app/s/operational-schedule',
          show: isRootAdmin,
        },
        {
          icon: CheckSquare,
          label: 'Approval Requests',
          path: '/app/s/approval-list',
          // Hidden from sidebar — but accessible via direct URL if user has permission
          show: false,
          accessGate: canApprove,
        },
      ],
    },
    {
      label: 'Data & Monitoring',
      items: [
        {
          icon: Database,
          label: 'Data Sources',
          path: '/app/s/datasources-admin',
          show: isTeamAdmin,
        },

        {
          icon: Rss,
          label: 'Feeds',
          path: '/app/s/feeds-admin',
          show: isRootAdmin && features.feedsCreate,
        },
        {
          icon: Link,
          label: 'Share Links',
          path: '/app/s/share-links-admin',
          show: isTeamAdmin && features.shareLink,
        },
        {
          icon: BarChart3,
          label: 'Proof of Play',
          path: '/app/s/proof-of-play-settings',
          show: isRootAdmin,
        },
        {
          icon: Bell,
          label: 'Monitoring Alerts',
          path: '/app/s/monitoring-alert-rules',
          show: isRootAdmin,
        },
        {
          icon: ScrollText,
          label: 'Audit Log',
          path: '/app/security-audit-logging',
          show: isRootAdmin,
        },
      ],
    },
    {
      label: 'Advanced',
      items: [
        {
          icon: Shield,
          label: 'Branding Settings',
          path: '/app/s/branding-settings',
          show: isRootAdmin,
        },
        {
          icon: Fingerprint,
          label: 'SAML Single Sign On',
          path: '/app/s/saml-settings',
          show: isRootAdmin && !isWhiteLabel,
        },
        {
          icon: Cpu,
          label: 'Provisioning Templates',
          path: '/app/s/provisioning-templates',
          show: isRootAdmin && features.massProvision,
        },
        {
          icon: Radio,
          label: 'External Communications',
          path: '/app/s/external-coms',
          show: isRootAdmin,
        },
        {
          icon: MapPin,
          label: 'Functional Locations',
          path: '/app/s/functional-locations',
          show: isRootAdmin,
        },
        {
          icon: Key,
          label: 'API Keys',
          path: '/app/s/apikeys',
          show: isRootAdmin && !isWhiteLabel,
        },
        { icon: Plug, label: 'Integrations', path: '/app/s/integrations', show: isTeamAdmin },
      ],
    },
  ];
}

export function SettingsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navGroups = useSettingsNav();
  const features = useCompanyFeatures();
  const me = useAuthStore((s) => s.me);

  // Determine if current route is native or iframe
  const slug = location.pathname.replace('/app/s/', '');
  const isNativePage = NATIVE_SETTINGS_SLUGS.has(slug);

  // Access check: redirect users who don't have permission to view this settings page.
  // Build a map of path → NavItem for quick lookup.
  const currentNavItem = useMemo(() => {
    for (const group of navGroups) {
      for (const item of group.items) {
        if (item.path === location.pathname) return item;
      }
    }
    return null;
  }, [navGroups, location.pathname]);

  // Redirect if the nav item exists but the user lacks permission.
  // accessGate (when defined) overrides show for direct-URL access control.
  const isAccessDenied =
    !!me &&
    !!currentNavItem &&
    (() => {
      if (currentNavItem.accessGate !== undefined) return !currentNavItem.accessGate;
      return currentNavItem.show === false;
    })();

  useEffect(() => {
    if (isAccessDenied) {
      toast.error(t('You do not have permission to access this page.'), {
        id: 'settings-access-denied',
      });
      navigate('/app/screenManagement', { replace: true });
    }
  }, [isAccessDenied, navigate, t]);

  // Plan gate: show upgrade screen instead of iframe when feature flag is off
  const planGateConfig = SETTINGS_PLAN_GATES[slug] ?? null;
  const isPlanGated = planGateConfig !== null && !features[planGateConfig.planFeature];

  // Resolve target scio1 state for the iframe
  const routeKey = location.pathname.replace('/app/', '');
  const targetState = SCIO1_STATE_MAP[routeKey];

  // Redirect scio1-only slugs that have no scio3 equivalent
  useEffect(() => {
    const target = SETTINGS_REDIRECTS[slug];
    if (target) navigate(target, { replace: true });
  }, [slug, navigate]);

  // Hide sidebar for pages that should be full-width (e.g. order-device is an external shop page)
  const hideSidebar = FULL_WIDTH_SETTINGS_SLUGS.has(slug);

  // Don't render anything while redirecting due to access denial
  if (isAccessDenied) return null;

  return (
    <div className="flex h-full bg-muted">
      <aside className={cn('hidden w-60 shrink-0 lg:block', hideSidebar && 'lg:hidden')}>
        <ScrollArea className="h-full py-3">
          <nav className="space-y-4 px-2">
            {navGroups.map((group) => {
              const visibleItems = group.items.filter((item) => item.show !== false);
              if (visibleItems.length === 0) return null;

              return (
                <div key={group.label}>
                  <h3 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {t(group.label)}
                  </h3>
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      const Icon = item.icon;

                      return (
                        <Button
                          key={item.path}
                          variant="ghost"
                          size="sm"
                          className={cn(
                            'w-full justify-start gap-2.5 rounded-md px-3 text-[13px] font-normal',
                            isActive
                              ? 'bg-sidebar-active font-medium text-foreground hover:bg-sidebar-active'
                              : 'text-muted-foreground hover:bg-sidebar-active/60 hover:text-foreground',
                          )}
                          onClick={() => navigate(item.path)}
                        >
                          <Icon
                            className={cn(
                              'h-4 w-4 shrink-0',
                              isActive ? 'text-primary' : 'text-muted-foreground/70',
                            )}
                          />
                          {t(item.label)}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </ScrollArea>
      </aside>
      <main className="flex min-h-0 flex-1 p-2">
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-card">
          {/* Plan gate overlay — shown on top of iframe when feature flag is off */}
          {isPlanGated && planGateConfig && <PlanGate {...planGateConfig} />}
          {/* Native page content rendered via Outlet */}
          {isNativePage && !isPlanGated && (
            <div className="flex h-full flex-col overflow-y-scroll">
              <Outlet />
            </div>
          )}
          {/* Persistent iframe — h-full fills the flex-1 wrapper height */}
          <div
            className={cn(
              'min-h-0 flex-1 overflow-hidden',
              (isNativePage || isPlanGated) && 'hidden',
            )}
          >
            <Scio1Frame targetState={targetState} className="h-full w-full border-0" />
          </div>
        </div>
      </main>
    </div>
  );
}
